import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Box,
  Typography,
  CircularProgress,
  Alert as MuiAlert,
  IconButton,
} from '@mui/material';
import CloseIcon from '@mui/icons-material/Close';
import { LineChart, Line, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, Legend } from 'recharts';
import dashboardService, { type AlertMetricsHistoryStats } from '../../services/dashboardService';

interface AlertMetricsDialogProps {
  open: boolean;
  onClose: () => void;
}

const SEVERITY_COLORS = {
  critical: '#E31B23',
  high: '#F39C12',
  medium: '#3498DB',
  low: '#27AE60',
};

const AlertMetricsDialog: React.FC<AlertMetricsDialogProps> = ({ open, onClose }) => {
  const [data, setData] = useState<AlertMetricsHistoryStats | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!open) return;

    const fetchData = async () => {
      setLoading(true);
      setError(null);
      try {
        const result = await dashboardService.getAlertMetricsHistory();
        setData(result);
      } catch (err) {
        setError(err instanceof Error ? err.message : 'Failed to fetch alert metrics history');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [open]);

  const formatChartData = (stats: AlertMetricsHistoryStats) => {
    return stats.historyPoints.map(point => ({
      timestamp: point.snapshotTime.toLocaleString('en-US', {
        month: 'short',
        day: 'numeric',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }),
      fullTimestamp: point.snapshotTime.toLocaleString(),
      Critical: point.criticalCount,
      High: point.highCount,
      Medium: point.mediumCount,
      Low: point.lowCount,
    }));
  };

  return (
    <Dialog open={open} onClose={onClose} maxWidth="lg" fullWidth>
      <DialogTitle sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        Alert Metrics (Last 7 Days)
        <IconButton onClick={onClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 6 }}>
            <CircularProgress />
          </Box>
        ) : error ? (
          <MuiAlert severity="error" sx={{ mb: 2 }}>
            {error}
          </MuiAlert>
        ) : data && data.historyPoints.length > 0 ? (
          <Box sx={{ height: 400, width: '100%', mt: 1 }}>
            <ResponsiveContainer width="100%" height="100%">
              <LineChart data={formatChartData(data)}>
                <CartesianGrid strokeDasharray="3 3" />
                <XAxis dataKey="timestamp" tick={{ fontSize: 11 }} interval="preserveStartEnd" />
                <YAxis allowDecimals={false} />
                <Tooltip
                  labelFormatter={(label, payload) => {
                    const item = payload?.[0]?.payload;
                    return item?.fullTimestamp || label;
                  }}
                />
                <Legend />
                <Line
                  type="monotone"
                  dataKey="Critical"
                  stroke={SEVERITY_COLORS.critical}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="High"
                  stroke={SEVERITY_COLORS.high}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="Medium"
                  stroke={SEVERITY_COLORS.medium}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
                <Line
                  type="monotone"
                  dataKey="Low"
                  stroke={SEVERITY_COLORS.low}
                  strokeWidth={2}
                  dot={false}
                  activeDot={{ r: 4 }}
                />
              </LineChart>
            </ResponsiveContainer>
          </Box>
        ) : (
          <Typography variant="body2" color="text.secondary" sx={{ textAlign: 'center', py: 6 }}>
            No alert metrics data available yet. Data is collected every 15 minutes.
          </Typography>
        )}
      </DialogContent>
    </Dialog>
  );
};

export default AlertMetricsDialog;
